/**
 * Générateur — Automatisme : reconnaître une bissectrice.
 * Attendu ID : 1074 | Niveau : 5ème | Domaine : Angles
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Est-ce la bissectrice ?         (QCM conclusion + raison sur figure ; V/F sur la figure)
 *   (la carte « La mesure des deux angles » est retirée : bissectrice donnée, pas reconnue — 2026-09-04)
 *   - Bissectrice : vrai ou faux ?    (V/F pool du modèle + deux lignes numériques)
 *
 * ── Plan de cartes validé (2026-09-04) ─────────────────────────────────────
 * Source : Automatisme n°36 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : bissectrice INTRODUITE en 6e (définition d'un angle saillant, construction
 * au rapporteur), basculée en automatisme en 5e ; le CM2 n'apporte que la
 * comparaison d'angles et le codage.
 * P2 : sens installés — la définition à DEUX conditions (issue du sommet et
 * intérieure ; partage en deux angles de même mesure) et le geste de
 * construction (reporter la moitié). Distracteurs sur les deux confusions de la
 * fiche : l'égalité sans la position (demi-droite EXTÉRIEURE portant la moitié,
 * question c) et la position sans l'égalité (intérieure mais 70 ≠ 50, question b).
 * P3 : mesures entières, moitiés d'angles PAIRS, une soustraction au plus.
 * P5 : angle xOy et demi-droite [Ot), codage d'égalité d'angles (arcs à un
 * trait) ou mesures écrites.
 * P6/P10 : pas de rapporteur ; « le codage fait foi, aucune mesure n'est
 * nécessaire » (question d) ; la raison cite la position ET l'égalité.
 * P7 : angle saillant seulement (pas d'angle plat, pas d'angle rentrant).
 * P8 : exigibles depuis la 6e — bissectrice, [Ot), angle xOy, codage d'égalité.
 * P11 : « vérifier deux choses, jamais une seule » (conclusion de la fiche).
 * P12 : rémanence forte, pas d'item de réactivation.
 * INVARIANTS :
 *   - la configuration est CALCULÉE (demi-droites en degrés entiers) : position
 *     (intérieure / extérieure) et égalité des deux angles RECALCULÉES, jamais
 *     écrites à la main ; tous les angles servis sont FRANCS (25° à 70°, 110° à
 *     155°) pour que rien ne se lise « presque » ;
 *   - le dessin ne contredit jamais le codage ; allure trompeuse autorisée (une
 *     demi-droite dessinée au milieu mais codée d'un seul côté) ;
 *   - data-rays, data-codes et data-valeurs dans le SVG pour les solveurs ;
 *     réponses ENTIÈRES ; pas de calculatrice ;
 *   - V/F : fenêtre stricte des 4 dernières lignes fixes servies.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1074;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_RECONNAITRE = 'Est-ce la bissectrice ?';
const L_VF = 'Bissectrice : vrai ou faux ?';

const mod360 = (x) => ((x % 360) + 360) % 360;
const estFranc = (m) => (m >= 25 && m <= 70) || (m >= 110 && m <= 155);

// Jeux de lettres (fiche : x, y, t ; z, w, u ; r, s, k).
const LETTRES = [['x', 'y', 't'], ['z', 'w', 'u'], ['r', 's', 'k'], ['a', 'b', 'c']];

// ─── Configuration : angle xOy et demi-droite [Ot) ─────────────────────────
// rayons : { x: θ, y: θ + φ, t: θ + a } (degrés, sens trigo). [Ot) est
// INTÉRIEURE si 0 < a < φ. Les deux angles xOt et tOy valent a et φ − a
// (intérieure) ; extérieure : a < 0 (de l'autre côté de [Ox)).

/**
 * @param {'oui'|'une_marque'|'mesures_differentes'|'mesures_egales'|'exterieure'} cas
 */
const construireConfiguration = (cas) => {
    const [nx, ny, nt] = randomChoice(LETTRES);
    const theta = randInt(0, 359);
    let phi; let a;
    for (let essai = 0; essai < 200; essai++) {
        // φ pair pour que la moitié soit entière ; φ et ses parts FRANCS.
        phi = 2 * randInt(25, 77);   // 50 … 154
        if (!estFranc(phi)) continue;
        if (cas === 'oui' || cas === 'une_marque' || cas === 'mesures_egales') a = phi / 2;
        else if (cas === 'mesures_differentes') { a = randInt(25, phi - 25); if (a === phi / 2) continue; }
        else a = -phi / 2;   // extérieure : de l'autre côté de [Ox), à la moitié de φ (question c)
        const parts = cas === 'exterieure' ? [phi / 2] : [a, phi - a];
        if (parts.every(estFranc)) break;
        phi = null;
    }
    if (phi === null) throw new Error('gen_1074 : configuration introuvable');
    const rayons = { [nx]: mod360(theta), [ny]: mod360(theta + phi), [nt]: mod360(theta + a) };
    const interieure = a > 0 && a < phi;
    const egales = interieure && a === phi - a;
    // Gardes recalculées sur les rayons.
    const mes = (P, Q) => { const d = mod360(rayons[P] - rayons[Q]); return d > 180 ? 360 - d : d; };
    if (mes(nx, ny) !== phi) throw new Error('gen_1074 : ouverture contredite');
    if (interieure && mes(nx, nt) + mes(nt, ny) !== phi) throw new Error('gen_1074 : [Ot) dite intérieure ne partage pas l\'angle');
    if (!interieure && mes(nx, nt) + mes(nt, ny) === phi) throw new Error('gen_1074 : [Ot) dite extérieure partage l\'angle');
    return { noms: { x: nx, y: ny, t: nt }, rayons, theta, phi, a, interieure, egales, cas };
};

// ─── Rendu SVG ─────────────────────────────────────────────────────────────

const W = 320; const H = 240; const O = [160, 124]; const RAYON = 92;
const f1 = (v) => v.toFixed(1);
const pointSur = (deg, r) => [O[0] + r * Math.cos((deg * Math.PI) / 180), O[1] - r * Math.sin((deg * Math.PI) / 180)];
const secteurTrigo = (rayons, P, Q) => (mod360(rayons[Q] - rayons[P]) <= 180 ? [rayons[P], rayons[Q]] : [rayons[Q], rayons[P]]);

const svgArcOuvert = (a1, a2, r, attrs) => {
    const sweep = mod360(a2 - a1);
    if (sweep === 0) return '';
    const p1 = pointSur(a1, r); const p2 = pointSur(a2, r);
    return `<path d="M ${f1(p1[0])} ${f1(p1[1])} A ${r} ${r} 0 ${sweep > 180 ? 1 : 0} 0 ${f1(p2[0])} ${f1(p2[1])}" fill="none" ${attrs}/>`;
};

/** Codage d'égalité d'angles : arc + un petit trait sur la bissectrice de l'arc. */
const svgCodageAngle = (a1, a2, r, nom) => {
    const sweep = mod360(a2 - a1); const bis = a1 + sweep / 2;
    const p1 = pointSur(bis, r - 5); const p2 = pointSur(bis, r + 5);
    return svgArcOuvert(a1, a2, r, 'stroke="#B45309" stroke-width="1.6"')
        + `<line x1="${f1(p1[0])}" y1="${f1(p1[1])}" x2="${f1(p2[0])}" y2="${f1(p2[1])}" stroke="#B45309" stroke-width="2" data-code="egal" data-angle="${nom}"/>`;
};

const positionEtiquetteO = (rayons) => {
    const angles = Object.values(rayons).map(mod360).sort((u, v) => u - v);
    let meilleur = 0; let ecartMax = -1;
    for (let k = 0; k < angles.length; k++) {
        const suivant = k + 1 < angles.length ? angles[k + 1] : angles[0] + 360;
        const ecart = suivant - angles[k];
        if (ecart > ecartMax) { ecartMax = ecart; meilleur = mod360(angles[k] + ecart / 2); }
    }
    const recul = Math.min(40, Math.max(16, 9 / Math.sin((Math.max(ecartMax, 10) * Math.PI) / 360)));
    return pointSur(meilleur, recul);
};

/**
 * options.codes   : ['xt', 'ty'] → codage d'égalité sur ces angles
 * options.valeurs : { xt: '50°', ty: '30°', xy: '?' }
 * options.arcs    : [['x','y']] → arc fin sans codage
 */
const svgFigure = (config, options = {}) => {
    const { rayons, noms } = config;
    let s = '';
    const angleDe = (cle) => [noms[cle[0]] || cle[0], noms[cle[1]] || cle[1]];
    (options.arcs || []).forEach((paire, i) => {
        const [P, Q] = angleDe(paire.join(''));   // rôles x, y, t → lettres réelles
        const [a1, a2] = secteurTrigo(rayons, P, Q);
        // L'arc de l'angle ENTIER xOy passe au large des codages des deux moitiés (r = 62).
        const rArc = paire.join('') === 'xy' ? 62 : 30 + i * 6;
        s += svgArcOuvert(a1, a2, rArc, 'stroke="#B45309" stroke-width="1.4" stroke-dasharray="4 3"');
    });
    (options.codes || []).forEach((cle, i) => {
        const [P, Q] = angleDe(cle);
        const [a1, a2] = secteurTrigo(rayons, P, Q);
        s += svgCodageAngle(a1, a2, 22 + i * 5, cle);
    });
    Object.entries(rayons).forEach(([nom, deg]) => {
        const P = pointSur(deg, RAYON);
        s += `<line x1="${f1(O[0])}" y1="${f1(O[1])}" x2="${f1(P[0])}" y2="${f1(P[1])}" stroke="#1E3A8A" stroke-width="2.2"/>`;
        const L = pointSur(deg, RAYON + 13);
        s += `<text x="${f1(L[0])}" y="${f1(L[1] + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${nom}</text>`;
    });
    Object.entries(options.valeurs || {}).forEach(([cle, txt], i) => {
        const [P, Q] = angleDe(cle);
        const [a1, a2] = secteurTrigo(rayons, P, Q);
        // Un arc sous chaque mesure écrite (demande prof 2026-09-04), sauf si l'angle est déjà codé.
        const dejaTrace = (options.codes || []).includes(cle) || (options.arcs || []).some((paire) => paire.join('') === cle);
        if (!dejaTrace) s += svgArcOuvert(a1, a2, 24 + i * 5, 'stroke="#B45309" stroke-width="1.5"');
        const sweep = mod360(a2 - a1); const bis = a1 + sweep / 2;
        // La mesure de l'angle ENTIER xOy n'est pas posée sur la bissectrice (c'est là que passe [Ot)) :
        // elle est portée juste au-delà de son arc, dans la moitié côté y (retour prof 2026-09-04).
        const recul = Math.max(46, 12 / Math.tan((Math.max(sweep, 8) * Math.PI) / 360) + 8);
        const X = cle === 'xy' ? pointSur(a1 + sweep * 0.72, 80) : pointSur(bis, Math.min(recul, 76));
        s += `<text x="${f1(X[0])}" y="${f1(X[1] + 4)}" font-size="12.5" font-weight="700" fill="#B45309" text-anchor="middle" data-valeur="${cle}">${txt}</text>`;
    });
    const lO = positionEtiquetteO(rayons);
    s += `<circle cx="${f1(O[0])}" cy="${f1(O[1])}" r="2.6" fill="#1e293b"/>`
        + `<text x="${f1(lO[0])}" y="${f1(lO[1] + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">O</text>`;
    const dataRays = Object.entries(rayons).map(([n, d]) => `${n}:${d}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-rays="${dataRays}" data-noms="${noms.x}${noms.y}${noms.t}">${s}</svg>`;
};

// Nom d'angle avec le chapeau sur le sommet : xÔy (accent circonflexe combinant, demande prof 2026-09-04).
const CHAPEAU = '̂';
const nomAngle = (noms, P, Q) => `${noms[P]}O${CHAPEAU}${noms[Q]}`;
const nomDemi = (noms) => `[O${noms.t})`;
// Dans les lignes de V/F : PAS de crochets (la clé de correction est [ligne|Vrai], les
// crochets d'une notation [Ot) la cassent — harnais RT-ELEVE-PARFAIT). On écrit « la demi-droite Ot ».
const nomDemiVf = (noms) => `la demi-droite O${noms.t}`;

// ─── Carte 1a : est-ce la bissectrice ? (QCM conclusion + raison) ──────────

const OPTIONS = (noms) => ({
    oui: `Oui : ${nomDemi(noms)} est à l'intérieur de l'angle ${nomAngle(noms, 'x', 'y')} et le partage en deux angles de même mesure.`,
    pas_egaux: `Non : ${nomDemi(noms)} est à l'intérieur de l'angle ${nomAngle(noms, 'x', 'y')}, mais les deux angles n'ont pas la même mesure.`,
    pas_etabli: `Non : rien n'indique que les angles ${nomAngle(noms, 'x', 't')} et ${nomAngle(noms, 't', 'y')} ont la même mesure.`,
    exterieure: `Non : ${nomDemi(noms)} n'est pas à l'intérieur de l'angle ${nomAngle(noms, 'x', 'y')}.`,
});

const CONCLUSION_PAR_CAS = { oui: 'oui', mesures_egales: 'oui', une_marque: 'pas_etabli', mesures_differentes: 'pas_egaux', exterieure: 'exterieure' };

let derniereSignatureQcm1074 = null;

const genReconnaitreQcm = () => {
    let payload = construireReconnaitreQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureQcm1074; t++) payload = construireReconnaitreQcm();
    derniereSignatureQcm1074 = payload._meta.params.signature;
    return payload;
};

const habillage = (config) => {
    // Ce que la figure MONTRE selon le cas : codages ou mesures écrites.
    const { cas, phi, a, noms } = config;
    if (cas === 'oui') return { codes: ['xt', 'ty'] };
    if (cas === 'une_marque') return { codes: ['xt'] };
    if (cas === 'mesures_egales') return { valeurs: { xt: `${a}°`, ty: `${phi - a}°` } };
    if (cas === 'mesures_differentes') return { valeurs: { xt: `${a}°`, ty: `${phi - a}°` } };
    // extérieure : xOy et xOt écrits, xOt = φ/2
    void noms;
    return { valeurs: { xy: `${phi}°`, xt: `${phi / 2}°` }, arcs: [['x', 'y']] };
};

const construireReconnaitreQcm = () => {
    const cas = randomChoice(['oui', 'une_marque', 'mesures_egales', 'mesures_differentes', 'exterieure']);
    const config = construireConfiguration(cas);
    const { noms } = config;
    const O_ = OPTIONS(noms);
    const bonne = O_[CONCLUSION_PAR_CAS[cas]];
    const autres = Object.keys(O_).filter((k) => k !== CONCLUSION_PAR_CAS[cas]).map((k) => O_[k]);
    // Garde : la conclusion recalculée sur la configuration est cohérente avec le cas.
    if ((CONCLUSION_PAR_CAS[cas] === 'oui') !== (config.interieure && config.egales && cas !== 'une_marque')) throw new Error('gen_1074 : conclusion contredite');
    const texte = `<p>La figure montre l'angle ${nomAngle(noms, 'x', 'y')} et la demi-droite ${nomDemi(noms)}.</p><p>${nomDemi(noms)} est-elle la bissectrice de l'angle ${nomAngle(noms, 'x', 'y')} ?</p>`;
    const svg = svgFigure(config, habillage(config));
    const payload = makeQcm([bonne, ...autres], bonne, texte, {
        ...opts(L_RECONNAITRE), elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement', sousType: cas };
    payload._meta.params = { cas, phi: config.phi, a: config.a, signature: `${cas}-${config.phi}-${config.a}-${noms.x}` };
    return payload;
};

// ─── Carte 1b : V/F sur la figure ──────────────────────────────────────────
// Configurations à mesures ÉCRITES seulement (toutes les lignes sont décidables).

let dernieresLignesVfFigure1074 = [];

const genFigureVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        const cas = randomChoice(['mesures_egales', 'mesures_differentes', 'exterieure']);
        const config = construireConfiguration(cas);
        const { noms, phi, a, interieure, egales } = config;
        const xOy = nomAngle(noms, 'x', 'y'); const xOt = nomAngle(noms, 'x', 't'); const tOy = nomAngle(noms, 't', 'y');
        const valeurXy = Math.random() < 0.5 ? phi : phi + randomChoice([-10, 10]);
        const candidates = [
            { l: `La demi-droite O${noms.t} est à l'intérieur de l'angle ${xOy}.`, v: interieure },
            { l: `Les angles ${xOt} et ${tOy} ont la même mesure.`, v: egales },
            { l: `La demi-droite O${noms.t} est la bissectrice de l'angle ${xOy}.`, v: interieure && egales },
            // Valeur juste une fois sur deux (revue 2026-09-04, A8) : la situation « mesures
            // égales » a besoin de lignes FAUSSES pour passer la mixité.
            { l: `L'angle ${xOy} mesure ${valeurXy}°.`, v: valeurXy === phi },
            { l: `La demi-droite O${noms.t} est à l'extérieur de l'angle ${xOy}.`, v: !interieure },
            { l: `L'angle ${xOt} mesure la moitié de l'angle ${xOy}.`, v: cas !== 'mesures_differentes' },
            { l: `Pour affirmer que ${nomDemiVf(noms)} est la bissectrice, il faut d'abord vérifier qu'elle est à l'intérieur de l'angle.`, v: true },
        ];
        void a;
        const lignes = shuffle(candidates).slice(0, 4);
        const nbFaux = lignes.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        // A8 (revue 2026-09-04) : « intérieure » + « même mesure » + « bissectrice » jamais
        // toutes les trois ensemble (la troisième est la définition, déduite des deux autres).
        const trio = [candidates[0], candidates[1], candidates[2]].filter((c) => lignes.includes(c)).length;
        if (trio === 3) continue;
        if (lignes.some((it) => dernieresLignesVfFigure1074.includes(it.l))) continue;
        dernieresLignesVfFigure1074 = [...dernieresLignesVfFigure1074, ...lignes.map((it) => it.l)].slice(-8);
        const svg = svgFigure(config, habillage(config));
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            `<p>La figure montre l'angle ${xOy} et la demi-droite ${nomDemi(noms)}. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
            { ...opts(L_RECONNAITRE), elements_additionnels: imgElement(svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement', sousType: cas };
        payload._meta.params = { cas, phi, a };
        return payload;
    }
    throw new Error('gen_1074 : V/F figure sans mixité après 60 essais');
};

// La carte « La mesure des deux angles » (moitié / double) a été retirée : la
// bissectrice y était DONNÉE, ce n'est pas la reconnaître (décision prof 2026-09-04).

// ─── Carte 3 : vrai ou faux ? ──────────────────────────────────────────────

const POOL_VF = [
    // Trois lignes testées par les numériques (partage en deux angles égaux, extérieure, position)
    // retirées du pool : elles étaient exclues à chaque tirage (revue 2026-09-04, A8).
    { l: 'Reconnaître une bissectrice, c\'est vérifier deux choses : sa position et l\'égalité des deux angles.', v: true },
    { l: 'La bissectrice d\'un angle est issue de son sommet.', v: true },
    { l: 'La bissectrice d\'un angle est à l\'intérieur de cet angle.', v: true },
    { l: 'Si la demi-droite est intérieure à l\'angle, le codage d\'égalité des deux angles suffit à reconnaître la bissectrice.', v: true },
    { l: 'La bissectrice d\'un angle droit forme deux angles de 45°.', v: true },
    { l: 'Un angle n\'a qu\'une seule bissectrice.', v: true },
    { l: 'La bissectrice d\'un angle de 120° forme deux angles de 90°.', v: false },
    { l: 'La bissectrice d\'un angle aigu partage cet angle en deux angles obtus.', v: false },
    { l: 'Pour vérifier une bissectrice codée, il faut mesurer au rapporteur.', v: false },
    { l: 'Un angle a deux bissectrices.', v: false },
    { l: 'La bissectrice d\'un angle droit forme deux angles de 90°.', v: false },
    { l: 'La bissectrice d\'un angle de 70° forme deux angles de 70°.', v: false },
];
if (POOL_VF.filter((it) => it.v).length !== 6) throw new Error('gen_1074 : pool V/F déséquilibré');

let derniersServisVf1074 = [];

const genVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        const [nx, ny, nt] = randomChoice(LETTRES);
        const noms = { x: nx, y: ny, t: nt };
        const xOy = nomAngle(noms, 'x', 'y'); const xOt = nomAngle(noms, 'x', 't'); const tOy = nomAngle(noms, 't', 'y');
        // Deux lignes NUMÉRIQUES, vérité tirée à pile ou face puis valeurs.
        const phi = 2 * randInt(25, 77);
        const vraie1 = Math.random() < 0.5;   // intérieure (vrai) ou extérieure (faux), la moitié étant portée dans les deux cas
        const vraie2 = Math.random() < 0.5;
        const p = randInt(25, 70); const q = vraie2 ? p : p + randomChoice([-10, -5, 5, 10]);
        const numeriques = [
            { l: `La demi-droite O${noms.t} est à l'${vraie1 ? 'intérieur' : 'extérieur'} de l'angle ${xOy}, qui mesure ${phi}°, et ${xOt} mesure ${phi / 2}° : c'est la bissectrice de ${xOy}.`, v: vraie1 },
            { l: `La demi-droite O${noms.t} est à l'intérieur de l'angle ${xOy}, ${xOt} mesure ${p}° et ${tOy} mesure ${q}° : c'est la bissectrice de ${xOy}.`, v: p === q },
        ];
        if (numeriques[0].v !== vraie1 || numeriques[1].v !== vraie2) throw new Error('gen_1074 : ligne numérique mal calibrée');
        // A8 : les lignes de définition testées par les numériques sont exclues.
        const exclues = [];
        if (phi === 90) exclues.push('La bissectrice d\'un angle droit forme deux angles de 45°.', 'La bissectrice d\'un angle droit forme deux angles de 90°.');
        if (phi === 120) exclues.push('La bissectrice d\'un angle de 120° forme deux angles de 90°.');
        const dispo = POOL_VF.filter((it) => !derniersServisVf1074.includes(it.l) && !exclues.includes(it.l));
        const fixes = shuffle(dispo).slice(0, 2);
        const tirage = shuffle([...numeriques, ...fixes]);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        derniersServisVf1074 = [...derniersServisVf1074, ...fixes.map((it) => it.l)].slice(-4);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1074 : V/F sans mixité après 60 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    reconnaitre_qcm_unique: genReconnaitreQcm,
    reconnaitre_tableau_vf: genFigureVf,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'reconnaitre', label: L_RECONNAITRE, formatsList: buildFormats('reconnaitre') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
